import React, { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { HeaderLogo } from 'containers/Header/Header.styles';
import { useSelector } from 'react-redux';
import { getMainCompanyLogoSelector } from 'store/modules/bootstrap/selectors';
import { SimpleTabs } from 'ui/SimpleTabs';
import { makeBookingManagerApi } from 'services/BookingManagerApi';
import { useReviewPageQueries } from './hooks/useReviewPageQueries';
import { Feedback } from './Feedback';
import { ReviewSummaryBox } from './ReviewSummaryBox';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { appendQueryParam } from 'utils/url';


const reviewSourceMap = {
  guests: { tabName: 'Guests Feedback', title: 'What guests said…', strengths: 'What guests love' },
  travelPartners: { tabName: 'Partner Feedback', title: 'What partners said…', strengths: 'What partners love' },
  pureEscapes: { tabName: 'Our Feedback', title: 'What our team said…', strengths: 'What our team loves' },
}

export const ReviewPage = () => {
  const match = useRouteMatch<{ hotelUuid: string }>();
  const { hotelUuid } = match.params;
  const mainCompanyLogo = useSelector(getMainCompanyLogoSelector);
  const { getHotelReviews } = useReviewPageQueries(makeBookingManagerApi(), hotelUuid);
  const reviewPageData = getHotelReviews.data?.data;

  const feedbackSections = useMemo(() => Object.entries(reviewPageData?.detailedSummaries || {})
    .filter(([, data]) =>
      data.strengths?.length > 0 || data.areasForImprovement?.length > 0
    )
    .map(([name, data]) => ({
      name: reviewSourceMap[name as keyof typeof reviewSourceMap]?.tabName || 'Other',
      title: reviewSourceMap[name as keyof typeof reviewSourceMap]?.title || 'What others said…',
      strengthTitle: reviewSourceMap[name as keyof typeof reviewSourceMap]?.strengths || 'What others love',
      strengths: data.strengths || [],
      areasForImprovement: data.areasForImprovement || []
    })), [reviewPageData]);

  // Combine summaries text for ReviewSummaryBox
  const summaryText = useMemo(() => {
    if (!reviewPageData?.summaries) return '';
    return [
      reviewPageData.summaries.guests?.replace(/\n/g, ''),
      reviewPageData.summaries.travelPartners?.replace(/\n/g, ''),
      reviewPageData.summaries.pureEscapes?.replace(/\n/g, '')
    ].filter(Boolean).join('\n');
  }, [reviewPageData]);

  if (getHotelReviews.isLoading) {
    return (
      <div className="container w-full min-w-full mx-auto mt-[100px]">
        <LoadingBar label="Loading content..." />
      </div>
    );
  }

  if (getHotelReviews.isError) {
    return (
      <div className="container w-full min-w-full mx-auto mt-[100px]">
        <ErrorBar />
      </div>
    );
  }

  return (
    <div>
      <div className="header h-100px w-full flex items-center justify-center bg-white shadow-[0px_5px_5px_-1px_rgba(15,23,42,0.03)] border-b border-gray-20">
        <HeaderLogo to="/">
          {mainCompanyLogo?.url && <img src={appendQueryParam(mainCompanyLogo?.url, 'isIcon', 'true')} alt='Company Logo' />}
        </HeaderLogo>
      </div>
      <div className="content mt-45px mx-80px max-[1439px]:mx-20px flex flex-col gap-20px">
        <div className='hotel-name text-black text-4xl font-normal font-noe-display'>Reviews - <span className='hotel-name-text text-2xl'>{reviewPageData?.hotelName}</span></div>
        {reviewPageData && (
          <ReviewSummaryBox
            generalScore={reviewPageData.generalScore}
            summaryText={summaryText}
            detailedScores={reviewPageData.detailedScores}
          />
        )}
        {
          feedbackSections.length > 0 && (
            <div className='review-feedback'>
              {
                feedbackSections.length > 1 ? (
                  <SimpleTabs
                    showBorders={true}
                    forceEqualTabWidths={true}
                    className="o:w-full"
                    tabConfig={feedbackSections.map((section) => ({
                      title: section.name,
                      name: section.name,
                      styles: 'min-w-150px',
                      renderContent: () => <Feedback strengths={section.strengths} areasForImprovement={section.areasForImprovement} title={section.title} strengthTitle={section.strengthTitle} />
                    }))}
                  />
                ) : (
                  <div className='border border-gray-40 px-20px py-30px'>
                    <Feedback strengths={feedbackSections[0].strengths} areasForImprovement={feedbackSections[0].areasForImprovement} title={feedbackSections[0].title} strengthTitle={feedbackSections[0].strengthTitle} />
                  </div>
                )
              }

            </div>
          )
        }

      </div>
    </div>
  );
};